import humps from 'humps';

export const API_BASE = process.env.REACT_APP_API_BASE;

const api = {
  login: `${API_BASE}/auth/local/`,
  register: `${API_BASE}/auth/local/register`,
  forgot: `${API_BASE}/auth/forgot-password`,
  reset: `${API_BASE}/auth/reset-password`,
  me: `${API_BASE}/users/me`,
  teams: `${API_BASE}/teams/`,
  tenants: `${API_BASE}/ms-tenants/`,
  diagnostics: (team: string, platform: string) =>
    `${API_BASE}/diagnostics?team=${team}&platform=${platform}`,
  teamDetails: (id: string) => `${API_BASE}/teams/${id}`,
  tenantDetails: (id: string) => `${API_BASE}/ms-tenants/${id}`,
  teamManagersDetails: (team: any, id: number) => `${API_BASE}/teams/${team}/managers/${id}`,
  tenantManagersDetails: (team: any, id: number) => `${API_BASE}/ms-tenants/${team}/managers/${id}`,
  join: `${API_BASE}/channels/join`,
  msJoin: `${API_BASE}/ms-groups/join`,
  leave: `${API_BASE}/channels/leave`,
  msLeave: `${API_BASE}/ms-groups/leave`,
  members: `${API_BASE}/members/`,
  msMembers: `${API_BASE}/ms-members/`,
  invitations: `${API_BASE}/invitations/`,
  summary: `${API_BASE}/monthly-summaries`,
  summaries: (team: string, platform: string) =>
    `${API_BASE}/monthly-summaries?team=${team}&platform=${platform}`,
  invitationDetails: (id: number) => `${API_BASE}/invitations/${id}`,
  slack: (token: string, language: string) =>
    `${API_BASE}/auth/slack/callback?sensla_key=${token}&lang=${language}`,
  microsoft: (token: string, language: string) =>
    `${API_BASE}/auth/microsoft/callback?sensla_key=${token}&lang=${language}`,
  resources: (query?: string) =>
    query ? `${API_BASE}/resources?${query}` : `${API_BASE}/resources`,
  skills: `${API_BASE}/skills`,
  surveys: (team: string, platform: string) =>
    `${API_BASE}/surveys?team=${team}&platform=${platform}`,
  answers: `${API_BASE}/answers`,
};

export default api;

enum RequestTypes {
  DELETE = 'DELETE',
  GET = 'GET',
  MULTIPART = 'MULTIPART',
  PUT = 'PUT',
  PATCH = 'PATCH',
  POST = 'POST',
}

const getHeaders = async (method: RequestTypes): Promise<any> => {
  const headers: any = {
    Accept: 'application/json',
    'Content-Type': 'application/json',
  };

  const { token } = JSON.parse(localStorage.getItem('auth') || '{}');
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  if (method === RequestTypes.MULTIPART) {
    delete headers['Content-Type']; // important, form data will not be sent without deleting this
  }

  return headers;
};

const parseBody = (method: RequestTypes, data?: any, literal?: boolean) => {
  if (method === RequestTypes.MULTIPART) {
    return data;
  } else if (literal) {
    return JSON.stringify(data);
  } else {
    return JSON.stringify(humps.decamelizeKeys(data));
  }
};

const fetchHelper = async (
  url: string,
  method: RequestTypes,
  data?: any,
  literal?: boolean
): Promise<any> => {
  if (url.indexOf('?') < 0) {
    url = url.endsWith('/') ? url : `${url}/`;
  }
  const attributes: any = {
    headers: await getHeaders(method),
    method: method === RequestTypes.MULTIPART ? RequestTypes.PATCH : method,
  };

  if (data) {
    attributes.body = parseBody(method, data, literal);
  }

  try {
    const response = await fetch(url, attributes);

    if (response.ok) {
      return humps.camelizeKeys(await response.json());
    } else {
      const body = await response.json();
      const message =
        Object.entries(body).length > 0 ? Object.entries(body)[0][1] : response.statusText;

      return { error: { status: response.status, statusText: message, body } };
    }
  } catch (e) {
    return { error: { statusText: e } };
  }
};

interface IRequest {
  delete: (url: string) => Promise<any>;
  get: (url: string) => Promise<any>;
  multipart: (url: string, body: any) => Promise<any>;
  patch: (url: string, data: any, literal?: boolean) => Promise<any>;
  post: (url: string, data: any, literal?: boolean) => Promise<any>;
  put: (url: string, data: any, literal?: boolean) => Promise<any>;
}

export const request: IRequest = {
  delete: (url: string) => {
    return fetchHelper(url, RequestTypes.DELETE);
  },
  get: (url: string) => {
    return fetchHelper(url, RequestTypes.GET);
  },
  multipart: (url: string, data: any) => {
    return fetchHelper(url, RequestTypes.MULTIPART, data);
  },
  patch: (url: string, data: any, literal?: boolean) => {
    return fetchHelper(url, RequestTypes.PATCH, data, literal);
  },
  post: (url: string, data: any, literal?: boolean) => {
    return fetchHelper(url, RequestTypes.POST, data, literal);
  },
  put: (url: string, data: any, literal?: boolean) => {
    return fetchHelper(url, RequestTypes.PUT, data, literal);
  },
};
